import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";

const KittyList = () => {
  const [kitties, setKitties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchKitties = async () => {
    try {
      const res = await api.get("/kitties");
      setKitties(res.data);
    } catch (err) {
      console.error("Error fetching kitties:", err.response || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pauseKitty = async (id) => {
    try {
      await api.post(`/kitties/${id}/pause`);
      fetchKitties();
    } catch (err) {
      console.error("Pause error:", err.response || err.message);
    }
  };

  const resumeKitty = async (id) => {
    try {
      await api.post(`/kitties/${id}/resume`);
      fetchKitties();
    } catch (err) {
      console.error("Resume error:", err.response || err.message);
    }
  };

  useEffect(() => {
    fetchKitties();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg font-medium text-gray-600 animate-pulse">
          Loading kitties...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 md:p-10">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl shadow-lg p-6 mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          🐾 Kitty Management
        </h1>
        <Link
          to="/jewellers/kitty/add"
          className="mt-4 sm:mt-0 bg-white text-purple-600 font-semibold px-5 py-2.5 rounded-lg shadow hover:bg-purple-100 transition-all duration-200"
        >
          + Add Kitty
        </Link>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto bg-white rounded-xl shadow-md border border-gray-200">
        <table className="min-w-full text-sm text-gray-700">
          <thead className="bg-gray-100 text-gray-800 text-sm uppercase font-semibold">
            <tr>
              <th className="px-6 py-3 text-left">ID</th>
              <th className="px-6 py-3 text-left">Kitty Name</th>
              <th className="px-6 py-3 text-left">Type</th>
              <th className="px-6 py-3 text-left">Status</th>
              <th className="px-6 py-3 text-left">Target Amount</th>
              <th className="px-6 py-3 text-left">Collected</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {kitties.length > 0 ? (
              kitties.map((kitty, index) => (
                <tr
                  key={kitty.id}
                  className={`border-b hover:bg-purple-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                >
                  <td className="px-6 py-3 font-medium">{kitty.id}</td>
                  <td className="px-6 py-3">{kitty.kitty_name}</td>
                  <td className="px-6 py-3 capitalize">{kitty.kitty_type}</td>
                  <td className="px-6 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${kitty.status === "active"
                        ? "bg-green-100 text-green-700"
                        : kitty.status === "paused"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-gray-100 text-gray-700"
                        }`}
                    >
                      {kitty.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 font-medium text-gray-800">
                    ₹{kitty.target_amount?.toLocaleString()}
                  </td>
                  <td className="px-6 py-3 text-gray-700">
                    ₹{kitty.collected_amount?.toLocaleString()}
                  </td>
                  <td className="px-6 py-3 text-center space-x-2">
                    <Link
                      to={`/jewellers/kitty/${kitty.id}/view`}
                      className="inline-block bg-green-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-green-600 transition"
                    >
                      View
                    </Link>

                    <Link
                      to={`/jewellers/kittybooking/${kitty.id}/booking`}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                    >
                      Token
                    </Link>

                    <Link
                      to={`/jewellers/kitty/${kitty.id}/edit`}
                      className="inline-block bg-blue-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-blue-600 transition"
                    >
                      Edit
                    </Link>

                    <Link
                      to={`/jewellers/kitty/${kitty.id}/ledger`}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                    >
                      Ledger
                    </Link>

                    {kitty.status === "active" ? (
                      <button
                        onClick={() => pauseKitty(kitty.id)}
                        className="inline-block bg-red-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-red-600 transition"
                      >
                        Pause
                      </button>
                    ) : (
                      <button
                        onClick={() => resumeKitty(kitty.id)}
                        className="inline-block bg-yellow-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-yellow-600 transition"
                      >
                        Resume
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="px-6 py-10 text-center text-gray-500 italic"
                >
                  No kitties found. Click{" "}
                  <b className="text-purple-600">“Add Kitty”</b> to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KittyList;
